import { Eye, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useVereAgirAllList } from '@/api/queries/ver-e-agir-queries'
import {
	InspectionDetailsSection,
	ResponsiveFormModal,
} from '@/components/commons'
import { DetailField } from '@/components/commons/detail-field'
import {
	INSPECTION_COLORS,
	InspectionBeforeAfterPhotos,
	InspectionCard,
	InspectionCardHeader,
	InspectionDetailsHeader,
	InspectionListFilters,
} from '@/components/inspections'
import {
	InspectionSummaryBadge,
	InspectionSummaryCard,
	InspectionSummaryCardBody,
	InspectionSummaryCardField,
	InspectionSummaryCardHeader,
	InspectionSummaryCardLoading,
	InspectionSummaryCardTitle,
} from '@/components/inspections/inspection-summary-card'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore, useProfileStore } from '@/store'
import type { VereAgirAllListParams, VereAgirOutput } from '@/types'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay } from '@/utils/formatDate'

const getStatusLabel = (status: string, t: (key: string) => string) => {
	if (status === 'IN_PROGRESS') {
		return t('inspections.seeAndAct.statusInProgress')
	}
	if (status === 'RESOLVED') {
		return t('inspections.seeAndAct.statusResolved')
	}
	return status
}

const getSituationLabel = (situation: string, t: (key: string) => string) => {
	if (situation === 'STOP_AND_FIX') {
		return t('inspections.seeAndAct.situationStopAndFix')
	}
	if (situation === 'FIX_AND_CONTINUE') {
		return t('inspections.seeAndAct.situationFixAndContinue')
	}
	return situation
}

const seeAndActStyle = {
	accentClassName: 'border-l-kaefer-grass-green',
	badgeClassName:
		'border-kaefer-grass-green/30 bg-kaefer-grass-green/15 text-kaefer-grass-green',
	badgeLabelKey: 'inspections.types.seeAndAct.title',
} as const

const SeeAndActDetails = ({ inspection }: { inspection: VereAgirOutput }) => {
	const { t } = useTranslation()
	const colors = INSPECTION_COLORS.grassGreen

	return (
		<div className="space-y-5 py-2">
			<InspectionDetailsHeader
				date={formatDateToDisplay(inspection.date)}
				createdBy={capitalize(inspection.createdBy.name)}
			/>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.common.branch')}
					value={inspection.cr.branchName}
				/>
				<DetailField
					label={t('inspections.common.contract')}
					value={inspection.cr.name}
				/>
				{inspection.customer.id && (
					<DetailField
						label={t('inspections.common.customer')}
						value={inspection.customer.name}
					/>
				)}
			</InspectionDetailsSection>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.seeAndAct.typology')}
					value={inspection.kind.name}
				/>
				<DetailField
					label={t('inspections.seeAndAct.risk')}
					value={inspection.risk.name}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection layout="vertical">
				<DetailField
					label={t('inspections.seeAndAct.description')}
					value={inspection.description}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection layout="vertical">
				<DetailField
					label={t('inspections.seeAndAct.immediateAction')}
					value={inspection.immediateAction}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.seeAndAct.responsible')}
					value={capitalize(inspection.responsible.name)}
				/>
				<DetailField
					label={t('inspections.seeAndAct.deadline')}
					value={formatDateToDisplay(inspection.dueDate)}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection
				showSeparator={
					!!(inspection.imagePath || inspection.resolutionImagePath)
				}
			>
				<DetailField
					label={t('inspections.seeAndAct.status')}
					value={getStatusLabel(inspection.status, t)}
				/>
				<DetailField
					label={t('inspections.seeAndAct.situation')}
					value={getSituationLabel(inspection.situation, t)}
				/>
			</InspectionDetailsSection>

			<InspectionBeforeAfterPhotos
				beforeSrc={inspection.imagePath}
				afterSrc={inspection.resolutionImagePath}
				beforeLabel={t('inspections.seeAndAct.photoBefore')}
				afterLabel={t('inspections.seeAndAct.photoAfter')}
				colors={colors}
			/>
		</div>
	)
}

export const SeeAndActListPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const userRole = useAuthStore((s) => s.user?.role)
	const { defaultBranchId } = useProfileStore()

	// Determina se usuário pode gerenciar filtros de branch
	const canManageBranchFilter =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedStatus, setSelectedStatus] = useState<string | undefined>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [selectedInspection, setSelectedInspection] =
		useState<VereAgirOutput | null>(null)
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [cursor, setCursor] = useState<string | undefined>()
	const [inspections, setInspections] = useState<VereAgirOutput[]>([])
	const [facets, setFacets] = useState<{
		branches: Array<{ id: string; name: string; count: number }>
		contracts: Array<{ id: string; name: string; count: number }>
		statuses: Array<{ value: string; count: number }>
		customers: Array<{ id: string; name: string; count: number }>
	}>({
		branches: [],
		contracts: [],
		statuses: [],
		customers: [],
	})
	const [pageInfo, setPageInfo] = useState<{
		nextCursor: string | null
		hasNext: boolean
	}>({
		nextCursor: null,
		hasNext: false,
	})

	const shouldShowCustomerFilter =
		facets.customers.length > 0 || Boolean(selectedCustomerId)

	const queryParams = useMemo<VereAgirAllListParams>(
		() => ({
			// Para usuários não-admin, usa automaticamente a branch do perfil
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			status: selectedStatus,
			customerId: shouldShowCustomerFilter ? selectedCustomerId : undefined,
			q: debouncedSearch.trim() || undefined,
			cursor,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedStatus,
			selectedCustomerId,
			shouldShowCustomerFilter,
			debouncedSearch,
			cursor,
		],
	)

	const {
		data: listData,
		isLoading,
		isFetching,
	} = useVereAgirAllList(queryParams)

	useEffect(() => {
		if (!listData) return

		setFacets({
			branches: listData.facets.branches ?? [],
			contracts: listData.facets.contracts ?? [],
			statuses: listData.facets.statuses ?? [],
			customers: listData.facets.customers ?? [],
		})
		setPageInfo(listData.pageInfo)

		setInspections((previous) => {
			if (!cursor) {
				return listData.items
			}

			const existingIds = new Set(previous.map((inspection) => inspection.id))
			const newItems = listData.items.filter(
				(inspection) => !existingIds.has(inspection.id),
			)
			return [...previous, ...newItems]
		})
	}, [listData, cursor])

	const resetListState = () => {
		setCursor(undefined)
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleSearchChange = (value: string) => {
		setSearch(value)
		resetListState()
	}

	const handleBranchChange = (value: string) => {
		setSelectedBranchId(value || undefined)
		setSelectedCrId(undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleContractChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleStatusChange = (value: string) => {
		setSelectedStatus(value || undefined)
		resetListState()
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
		resetListState()
	}

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const handleBack = () => {
		navigate(ROUTES.INSPECTIONS.INDEX)
	}

	const handleNewInspection = () => {
		navigate(ROUTES.INSPECTIONS.SEE_AND_ACT.NEW)
	}

	const handleViewInspection = (inspection: VereAgirOutput) => {
		setSelectedInspection(inspection)
		setDetailsOpen(true)
	}

	const handleLoadMore = () => {
		if (!pageInfo.hasNext || !pageInfo.nextCursor || isFetching) return
		setCursor(pageInfo.nextCursor)
	}

	const colors = INSPECTION_COLORS.grassGreen
	const hasMore = pageInfo.hasNext && Boolean(pageInfo.nextCursor)
	const isLoadingMore = isFetching && Boolean(cursor)
	const footerCountLabel =
		inspections.length === 1
			? t('inspections.seeAndAct.countSingular')
			: t('inspections.seeAndAct.countPlural')
	const footerText = `${hasMore ? `${inspections.length}+` : inspections.length} ${footerCountLabel}`
	const isInitialLoading = isLoading && inspections.length === 0

	const filterSelects = [
		// Filtro de Branch: visível apenas para ADMIN, MANAGER e SSMA_MANAGER
		...(canManageBranchFilter
			? [
					{
						id: 'see-and-act-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: handleBranchChange,
						options: facets.branches.map((branch) => ({
							value: branch.id,
							label: branch.name,
							count: branch.count,
						})),
						allLabel: t('inspections.seeAndAct.filterAll'),
					},
				]
			: []),
		{
			id: 'see-and-act-filter-contract',
			label: t('inspections.seeAndAct.filterByContract'),
			value: selectedCrId,
			onChange: handleContractChange,
			options: facets.contracts.map((contract) => ({
				value: contract.id,
				label: contract.name,
				count: contract.count,
			})),
			allLabel: t('inspections.seeAndAct.filterAll'),
		},
		{
			id: 'see-and-act-filter-status',
			label: t('inspections.seeAndAct.status'),
			value: selectedStatus,
			onChange: handleStatusChange,
			options: facets.statuses.map((status) => ({
				value: status.value,
				label: getStatusLabel(status.value, t),
				count: status.count,
			})),
			allLabel: t('inspections.seeAndAct.filterAll'),
		},
		...(shouldShowCustomerFilter
			? [
					{
						id: 'see-and-act-filter-customer',
						label: t('inspections.seeAndAct.filterByCustomer'),
						value: selectedCustomerId,
						onChange: handleCustomerChange,
						options: facets.customers.map((customer) => ({
							value: customer.id,
							label: customer.name,
							count: customer.count,
						})),
						allLabel: t('inspections.seeAndAct.filterAll'),
					},
				]
			: []),
	]
	const renderSeeAndActCard = (inspection: VereAgirOutput) => (
		<InspectionSummaryCard
			key={inspection.id}
			accentClassName={seeAndActStyle.accentClassName}
			onClick={() => handleViewInspection(inspection)}
		>
			<InspectionSummaryCardBody>
				<InspectionSummaryCardHeader
					badge={
						<InspectionSummaryBadge
							label={t(seeAndActStyle.badgeLabelKey)}
							className={seeAndActStyle.badgeClassName}
						/>
					}
					topMeta={
						<span className="text-xs text-muted-foreground">
							{formatDateToDisplay(inspection.date)}
						</span>
					}
				/>

				<InspectionSummaryCardTitle>
					{capitalize(inspection.description || inspection.cr.name)}
				</InspectionSummaryCardTitle>

				<div className="flex items-center justify-between gap-2 text-xs">
					<div className="min-w-0 flex-1 space-y-2">
						<InspectionSummaryCardField
							label="Criado por: "
							value={capitalize(inspection.createdBy.name)}
						/>
						<InspectionSummaryCardField
							label="Contrato: "
							value={capitalize(inspection.cr.name)}
						/>
					</div>
				</div>
			</InspectionSummaryCardBody>
		</InspectionSummaryCard>
	)

	return (
		<>
			<InspectionCard colors={colors}>
				<InspectionCardHeader
					icon={<Eye className="size-5" />}
					title={t('inspections.types.seeAndAct.title')}
					subtitle={t('inspections.seeAndAct.listSubtitle')}
					colors={colors}
					onBack={handleBack}
					actions={
						<Button
							onClick={handleNewInspection}
							className="w-full gap-2 bg-kaefer-grass-green font-semibold text-white hover:bg-kaefer-grass-green/90 sm:w-auto"
						>
							<Plus className="size-4" />
							{t('inspections.seeAndAct.newInspection')}
						</Button>
					}
				/>
				<CardContent className="flex-1 space-y-4">
					<InspectionListFilters
						searchValue={search}
						onSearchChange={handleSearchChange}
						selects={filterSelects}
					/>

					{isSearchPending && inspections.length > 0 && (
						<InspectionSummaryCardLoading
							accentClassName={seeAndActStyle.accentClassName}
						/>
					)}

					{isInitialLoading ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{Array.from({ length: 6 }, (_, index) => (
								<InspectionSummaryCardLoading
									key={`see-and-act-loading-${index}`}
									accentClassName={seeAndActStyle.accentClassName}
								/>
							))}
						</div>
					) : inspections.length > 0 ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{inspections.map((inspection) => renderSeeAndActCard(inspection))}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
							<Eye className="size-10 text-kaefer-silver" />
							<p className="mt-3 text-sm text-muted-foreground">
								{t('inspections.seeAndAct.emptyList')}
							</p>
						</div>
					)}

					{hasMore && inspections.length > 0 && (
						<div className="flex justify-center">
							<Button
								variant="outline"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
								className="gap-2"
							>
								{isLoadingMore ? (
									<>
										<div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
										{t('inspections.seeAndAct.loadingMore')}
									</>
								) : (
									<>
										<Plus className="size-4" />
										{t('inspections.seeAndAct.loadMore')}
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
				<CardFooter className="mt-4 border-t border-kaefer-grass-green/30 bg-kaefer-grass-green/5 py-3">
					<p className="w-full text-xs text-muted-foreground">{footerText}</p>
				</CardFooter>
			</InspectionCard>
			<ResponsiveFormModal
				open={detailsOpen}
				onOpenChange={setDetailsOpen}
				title={t('inspections.seeAndAct.details')}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
			>
				{selectedInspection && (
					<SeeAndActDetails inspection={selectedInspection} />
				)}
			</ResponsiveFormModal>
		</>
	)
}
